import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Button, Row, Col, Spin, Table, Input, Select, Space, Alert } from 'antd';
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage';
import { wtApiCall } from 'src/helpers/commons/ApiWrapper';
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications';
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector';
import WidgetItemUnitSelector from 'src/widgets/Selectors/WidgetItemUnitSelector';
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector';
import WidgetAccountsSelector from 'src/widgets/Selectors/WidgetAccountsSelector';
import BaseListing from 'src/BaseListings';
import { useDownloadExcel } from 'react-export-table-to-excel';

const statusOptions = [
  { value: '', label: 'All' },
  { value: 1, label: 'Yes' },
  { value: 0, label: 'No' },
];

const renderYesNo = (value) => {
  if (value === 1 || value === '1') return 'Yes';
  if (value === 0 || value === '0') return 'No';
  return '-';
};

const renderActiveState = (value) => {
  if (value === 1 || value === '1') return 'Active';
  if (value === 0 || value === '0') return 'Inactive';
  return '-';
};

const renderNature = (value) => {
  if (value === 1 || value === '1') return 'Inventory';
  if (value === 2 || value === '2') return 'Non-Inventory';
  return '-';
};

const renderProcurementType = (value) => {
  if (value === 1 || value === '1') return 'In-House';
  if (value === 2 || value === '2') return 'Local';
  return '-';
};

const renderCostType = (value) => {
  if (value === 1 || value === '1') return 'Fixed';
  if (value === 2 || value === '2') return 'Variable';
  return '-';
};

const normalizeItemRow = (item, index) => {
  const brands = item.item_brands || item.item_has_brands || [];
  const brandNames = Array.isArray(brands)
    ? brands
        .map((brand) => brand?.label || brand?.item_brand?.name || brand?.name || '-')
        .filter((name) => name && name !== '-')
    : [];
  const brandIds = Array.isArray(brands)
    ? brands
        .map((brand) => brand?.value ?? brand?.item_brand_id ?? brand?.id)
        .filter((id) => id !== undefined && id !== null && id !== '')
    : [];

  return {
    key: item.id ?? index + 1,
    id: item.id ?? '-',
    itemClassId: item.item_class_id ?? '-',
    itemClass: item.item_class?.name || '-',
    itemCategoryId: item.item_category_id ?? '-',
    itemCategory: item.item_category?.name || '-',
    itemUnitId: item.item_unit_id ?? '-',
    itemUnit: item.item_unit?.name || '-',
    unitCategoryId: item.unit_category_id ?? '-',
    unitCategory: item.unit_category?.name || '-',
    costAccountId: item.cost_account_id ?? '-',
    costAccount: item.cost_account?.name || '-',
    inventoryAccountId: item.inventory_account_id ?? '-',
    inventoryAccount: item.inventory_account?.name || '-',
    salesBranchId: item.sales_branch_id ?? '-',
    salesBranch: item.sales_branch?.name || '-',
    code: item.code || '-',
    name: item.name || '-',
    description: item.description || '-',
    nature: renderNature(item.nature),
    model: item.model || '-',
    procurementType: renderProcurementType(item.procurement_type),
    isSerialRequired: renderYesNo(item.is_serial_required),
    isBarcodeRequired: renderYesNo(item.is_barcode_required),
    costRequired: renderYesNo(item.cost_required),
    costType: renderCostType(item.cost_type),
    hsCode: item.hs_code || '-',
    hsCodeDescription: item.hs_code_description || '-',
    fbrUomDescription: item.hs_code_uom_description || '-',
    status: renderActiveState(item.status),
    createdBy: item.created_by?.name || '-',
    updatedBy: item.updated_by?.name || '-',
    deletedAt: item.deleted_at || '-',
    brands: brandNames.length > 0 ? brandNames.join(', ') : 'None',
    brandId: brandIds.length > 0 ? brandIds.join(', ') : '-',
  };
};

const ItemReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [items, setItems] = useState([]);
  const [itemUnits, setItemUnits] = useState([]);
  const [branches, setBranches] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState('');
  const [selectedItemId, setSelectedItemId] = useState(0);
  const [selectedHsCode, setSelectedHsCode] = useState('');
  const [selectedSerialRequired, setSelectedSerialRequired] = useState('');
  const [selectedBarcodeRequired, setSelectedBarcodeRequired] = useState('');
  const [selectedCostRequired, setSelectedCostRequired] = useState('');
  const [selectedItemUnitId, setSelectedItemUnitId] = useState(0);
  const [selectedItemUnitName, setSelectedItemUnitName] = useState('');
  const [selectedCostAccountId, setSelectedCostAccountId] = useState(0);
  const [selectedCostAccountName, setSelectedCostAccountName] = useState('');
  const [selectedInventoryAccountId, setSelectedInventoryAccountId] = useState(0);
  const [selectedInventoryAccountName, setSelectedInventoryAccountName] = useState('');
  const [selectedSalesBranchId, setSelectedSalesBranchId] = useState(0);
  const [selectedSalesBranchName, setSelectedSalesBranchName] = useState('');
  const [tableScrollY, setTableScrollY] = useState(420);
  const exportTableRef = useRef(null);

  const companyName = wtGetFromLocalStorage('company_name');
  const Base = new BaseListing('item_report');

  const hasAnyFilter = useMemo(
    () =>
      Boolean(
        selectedItemId ||
          selectedHsCode.trim() ||
          selectedSerialRequired !== '' ||
          selectedBarcodeRequired !== '' ||
          selectedCostRequired !== '' ||
          selectedItemUnitId ||
          selectedCostAccountId ||
          selectedInventoryAccountId ||
          selectedSalesBranchId,
      ),
    [
      selectedItemId,
      selectedHsCode,
      selectedSerialRequired,
      selectedBarcodeRequired,
      selectedCostRequired,
      selectedItemUnitId,
      selectedCostAccountId,
      selectedInventoryAccountId,
      selectedSalesBranchId,
    ],
  );

  const handleItemChange = (val) => {
    setSelectedItemId(val?.value || 0);
    setSelectedItemName(val?.label || '');
  };

  const handleItemUnitChange = (val) => {
    setSelectedItemUnitId(val?.value || 0);
    setSelectedItemUnitName(val?.label || '');
  };

  const handleBranchChange = (val) => {
    setSelectedSalesBranchId(val?.value || 0);
    setSelectedSalesBranchName(val?.label || '');
  };

  const handleCostAccountChange = (value, label) => {
    setSelectedCostAccountId(value || 0);
    setSelectedCostAccountName(label || '');
  };

  const handleInventoryAccountChange = (value, label) => {
    setSelectedInventoryAccountId(value || 0);
    setSelectedInventoryAccountName(label || '');
  };

  const applyReportRows = (rows) => {
    setTableData(rows.map((item, index) => normalizeItemRow(item, index)));
  };

  const fetchItemReport = () => {
    if (!hasAnyFilter) {
      lzNotifyErrorMessageFromResult('Please apply at least one filter before generating the report.');
      return;
    }

    setIsLoading(true);

    const payload = {
      ...(selectedItemId ? { item_id: selectedItemId } : {}),
      ...(selectedHsCode.trim() ? { hs_code: selectedHsCode.trim() } : {}),
      ...(selectedSerialRequired !== '' ? { is_serial_required: selectedSerialRequired } : {}),
      ...(selectedBarcodeRequired !== '' ? { is_barcode_required: selectedBarcodeRequired } : {}),
      ...(selectedCostRequired !== '' ? { cost_required: selectedCostRequired } : {}),
      ...(selectedItemUnitId ? { item_unit_id: selectedItemUnitId } : {}),
      ...(selectedCostAccountId ? { cost_account_id: selectedCostAccountId } : {}),
      ...(selectedInventoryAccountId ? { inventory_account_id: selectedInventoryAccountId } : {}),
      ...(selectedSalesBranchId ? { sales_branch_id: selectedSalesBranchId } : {}),
    };

    wtApiCall(
      'reports/item_report',
      payload,
      'get',
      (res) => {
        if (res?.success && res?.data) {
          const rows = Array.isArray(res.data) ? res.data : [res.data];
          applyReportRows(rows);
        } else {
          setTableData([]);
          lzNotifyErrorMessageFromResult('No data found.');
        }
        setIsLoading(false);
      },
      (err) => {
        lzNotifyErrorMessageFromResult(err);
        setIsLoading(false);
      },
    );
  };

  const clearFilters = () => {
    setSelectedItemId(0);
    setSelectedItemName('');
    setSelectedHsCode('');
    setSelectedSerialRequired('');
    setSelectedBarcodeRequired('');
    setSelectedCostRequired('');
    setSelectedItemUnitId(0);
    setSelectedItemUnitName('');
    setSelectedCostAccountId(0);
    setSelectedCostAccountName('');
    setSelectedInventoryAccountId(0);
    setSelectedInventoryAccountName('');
    setSelectedSalesBranchId(0);
    setSelectedSalesBranchName('');
    setTableData([]);
  };

  const { onDownload } = useDownloadExcel({
    currentTableRef: exportTableRef.current,
    filename: 'Item Report',
    sheet: 'Item Report',
  });

  useEffect(() => {
    Base.dropdownData('items,item_units,branches,accounts', {}, [setItems, setItemUnits, setBranches, setAccounts], 1);
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      setTableScrollY(Math.max(260, window.innerHeight - 420));
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const columns = [
    {
      title: 'Item ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
      fixed: 'left',
      render: (value) =>
        value && value !== '-' ? (
          <a href={`/items#edit:${value}`} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Class',
      dataIndex: 'itemClass',
      key: 'itemClass',
      width: 180,
    },
    {
      title: 'Category',
      dataIndex: 'itemCategory',
      key: 'itemCategory',
      width: 180,
    },
    {
      title: 'Unit',
      dataIndex: 'itemUnit',
      key: 'itemUnit',
      width: 120,
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 140,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 240,
      ellipsis: true,
    },
    {
      title: 'Nature',
      dataIndex: 'nature',
      key: 'nature',
      width: 120,
    },
    {
      title: 'Procurement Type',
      dataIndex: 'procurementType',
      key: 'procurementType',
      width: 150,
    },
    {
      title: 'Serial Required',
      dataIndex: 'isSerialRequired',
      key: 'isSerialRequired',
      width: 140,
    },
    {
      title: 'Barcode Required',
      dataIndex: 'isBarcodeRequired',
      key: 'isBarcodeRequired',
      width: 150,
    },
    {
      title: 'Cost Required',
      dataIndex: 'costRequired',
      key: 'costRequired',
      width: 130,
    },
    {
      title: 'Cost Type',
      dataIndex: 'costType',
      key: 'costType',
      width: 120,
    },
    {
      title: 'HS Code',
      dataIndex: 'hsCode',
      key: 'hsCode',
      width: 140,
    },
    {
      title: 'HS Code Desc',
      dataIndex: 'hsCodeDescription',
      key: 'hsCodeDescription',
      width: 240,
      ellipsis: true,
    },
    {
      title: 'FBR UoM Description',
      dataIndex: 'fbrUomDescription',
      key: 'fbrUomDescription',
      width: 240,
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    },
    {
      title: 'Item Class ID',
      dataIndex: 'itemClassId',
      key: 'itemClassId',
      width: 120,
    },
    {
      title: 'Category ID',
      dataIndex: 'itemCategoryId',
      key: 'itemCategoryId',
      width: 120,
    },
    {
      title: 'Unit ID',
      dataIndex: 'itemUnitId',
      key: 'itemUnitId',
      width: 100,
    },
    {
      title: 'Unit Category',
      dataIndex: 'unitCategory',
      key: 'unitCategory',
      width: 180,
    },
    {
      title: 'Cost Account',
      dataIndex: 'costAccount',
      key: 'costAccount',
      width: 180,
    },
    {
      title: 'Inventory Account',
      dataIndex: 'inventoryAccount',
      key: 'inventoryAccount',
      width: 180,
    },
    {
      title: 'Sales Branch',
      dataIndex: 'salesBranch',
      key: 'salesBranch',
      width: 180,
    },
    {
      title: 'Brands',
      dataIndex: 'brands',
      key: 'brands',
      width: 220,
      ellipsis: true,
    },
  ];

  const exportColumns = [...columns, { title: 'brand_id', dataIndex: 'brandId', key: 'brandId' }];

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
      }}
    >
      <Card
        style={{ minHeight: 'calc(100vh - 32px)' }}
        bodyStyle={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        title={
          <span style={{ fontSize: '22px', display: 'block', textAlign: 'center' }}>
            {companyName} Item Report
          </span>
        }
      >
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Item</label>
            <WidgetItemSelector
              items={items}
              onChange={handleItemChange}
              displayValue={selectedItemName}
              value={selectedItemId}
              reloadDropdownData={() => Base.dropdownData('items', {}, [setItems], 1)}
              placeHolder="Select an item"
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <label>HS Code</label>
            <Input
              value={selectedHsCode}
              onChange={(e) => setSelectedHsCode(e.target.value)}
              placeholder="Search HS code"
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Serial Required</label>
            <Select
              value={selectedSerialRequired}
              onChange={setSelectedSerialRequired}
              options={statusOptions}
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Barcode Required</label>
            <Select
              value={selectedBarcodeRequired}
              onChange={setSelectedBarcodeRequired}
              options={statusOptions}
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Cost Required</label>
            <Select
              value={selectedCostRequired}
              onChange={setSelectedCostRequired}
              options={statusOptions}
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Item Unit</label>
            <WidgetItemUnitSelector
              item_units={itemUnits}
              onChange={handleItemUnitChange}
              displayValue={selectedItemUnitName}
              value={selectedItemUnitId}
              reloadDropdownData={() => Base.dropdownData('item_units', {}, [setItemUnits], 1)}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Cost Account</label>
            <WidgetAccountsSelector
              data={accounts}
              onChange={handleCostAccountChange}
              displayValue={selectedCostAccountName}
              value={selectedCostAccountId}
              reloadDropdownData={() => Base.dropdownData('accounts', {}, [setAccounts], 1)}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Inventory Account</label>
            <WidgetAccountsSelector
              data={accounts}
              onChange={handleInventoryAccountChange}
              displayValue={selectedInventoryAccountName}
              value={selectedInventoryAccountId}
              reloadDropdownData={() => Base.dropdownData('accounts', {}, [setAccounts], 1)}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Sale Branch</label>
            <WidgetBranchSelector
              branches={branches}
              onChange={handleBranchChange}
              displayValue={selectedSalesBranchName}
              value={selectedSalesBranchId}
              reloadDropdownData={() => Base.dropdownData('branches', {}, [setBranches], 1)}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Space>
              <Button type="primary" onClick={fetchItemReport} disabled={isLoading}>
                {isLoading ? 'Generating...' : 'Generate'}
              </Button>
              <Button onClick={clearFilters} disabled={isLoading}>
                Clear
              </Button>
              <Button onClick={onDownload} disabled={isLoading || tableData.length === 0}>
                Export to Excel
              </Button>
            </Space>
          </Col>
        </Row>

        <div style={{ marginTop: 16 }}>
          {!hasAnyFilter && (
            <Alert
              type="info"
              showIcon
              message="Apply at least one filter before generating the report."
            />
          )}
        </div>

        <div style={{ flex: 1, minHeight: 0, marginTop: 16 }}>
          <table ref={exportTableRef} style={{ display: 'none' }}>
            <thead>
              <tr>
                {exportColumns.map((column) => (
                  <th key={column.key}>{column.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((record) => (
                <tr key={record.key}>
                  {exportColumns.map((column) => (
                    <td key={column.key}>{record[column.dataIndex] ?? '-'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: 'max-content', y: tableScrollY }}
            loading={{ indicator: <Spin />, spinning: isLoading }}
            rowKey="key"
          />
        </div>
      </Card>
    </div>
  );
};

export default ItemReport;
